import { useState, useCallback, useMemo } from "react";
import { CUSTOMER_ID_MAP } from "@/customers/idMap";

const STORAGE_KEY = "approved_results_filters";

export const APPROVED_RESULTS_FILTERS_DEFAULTS = {
  customerIds: [CUSTOMER_ID_MAP.UHCW, CUSTOMER_ID_MAP.RFT],
  fromDate: new Date("2026-02-01"),
};

interface ApprovedResultsFilters {
  customerIds: string[];
  fromDate: Date | null;
}

interface StoredFilters {
  customerIds: string[];
  fromDate: string | null;
}

function loadFromLocalStorage(): ApprovedResultsFilters | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed: StoredFilters = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.customerIds)) {
      return null;
    }
    return {
      customerIds: parsed.customerIds,
      fromDate: parsed.fromDate ? new Date(parsed.fromDate) : null,
    };
  } catch {
    console.warn("Failed to parse approved results filters from localStorage");
    return null;
  }
}

function saveToLocalStorage(filters: ApprovedResultsFilters) {
  const stored: StoredFilters = {
    customerIds: filters.customerIds,
    fromDate: filters.fromDate?.toISOString() ?? null,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

export function useApprovedResultsFilters() {
  const [filters, setFiltersState] = useState<ApprovedResultsFilters>(
    () => loadFromLocalStorage() ?? APPROVED_RESULTS_FILTERS_DEFAULTS,
  );

  const setFilters = useCallback((newFilters: ApprovedResultsFilters) => {
    setFiltersState(newFilters);
    saveToLocalStorage(newFilters);
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState(APPROVED_RESULTS_FILTERS_DEFAULTS);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const isCustomised = useMemo(() => {
    const defaults = APPROVED_RESULTS_FILTERS_DEFAULTS;
    if (filters.customerIds.length !== defaults.customerIds.length) return true;
    if (!filters.customerIds.every((id) => defaults.customerIds.includes(id))) {
      return true;
    }
    if (filters.fromDate?.getTime() !== defaults.fromDate.getTime()) {
      return true;
    }
    return false;
  }, [filters]);

  return {
    customerIds: filters.customerIds,
    fromDate: filters.fromDate,
    setFilters,
    clearFilters,
    isCustomised,
  };
}
